import {
  Badge,
  Box,
  Flex,
  Heading,
  Input,
  Spinner,
  Stack,
  Table,
  Text,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import {
  FiArrowLeft,
  FiEdit2,
  FiFolder,
  FiPlus,
  FiTrash2,
} from "react-icons/fi"

import { OpenAPI } from "@/client"
import { IconBox, VisionButton, VisionCard } from "@/components/VisionUI"
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/components/ui/dialog"
import { useOrganization } from "@/contexts/OrganizationContext"
import useCustomToast from "@/hooks/useCustomToast"
import { useSemanticColors } from "@/hooks/useSemanticColors"

export const Route = createFileRoute("/_layout/tag-categories")({
  component: TagCategoriesPage,
})

interface CategoryRow {
  id: string
  organisation_id: string
  name: string
  is_default?: boolean
  enabled?: boolean
}

function authedFetch(path: string, init: RequestInit = {}) {
  const token = localStorage.getItem("access_token")
  const base = (OpenAPI.BASE || "").replace(/\/$/, "")
  return fetch(`${base}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  })
}

function TagCategoriesPage() {
  const sc = useSemanticColors()
  const { t } = useTranslation("dpp")
  const navigate = useNavigate()
  const { currentOrganization } = useOrganization()
  const queryClient = useQueryClient()
  const { showToast } = useCustomToast()
  const orgId = currentOrganization?.id

  const listQuery = useQuery({
    queryKey: ["tag-categories", orgId],
    queryFn: async (): Promise<{ data: CategoryRow[]; count: number }> => {
      const r = await authedFetch(
        `/api/v1/organizations/${orgId}/tag-categories/?limit=500`,
      )
      if (!r.ok) throw new Error(await r.text())
      return r.json()
    },
    enabled: !!orgId,
    staleTime: 30_000,
  })
  const rows = listQuery.data?.data ?? []

  const [isOpen, setIsOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingIsDefault, setEditingIsDefault] = useState(false)
  const [draftName, setDraftName] = useState("")

  const resetDraft = () => {
    setEditingId(null)
    setEditingIsDefault(false)
    setDraftName("")
  }
  const seedFromRow = (row: CategoryRow) => {
    setEditingId(row.id)
    setEditingIsDefault(!!row.is_default)
    setDraftName(row.name)
    setIsOpen(true)
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!orgId) throw new Error("No organisation selected")
      const url = editingId
        ? `/api/v1/organizations/${orgId}/tag-categories/${editingId}`
        : `/api/v1/organizations/${orgId}/tag-categories/`
      const body = editingId
        ? { name: draftName.trim() }
        : { organisation_id: orgId, name: draftName.trim() }
      const res = await authedFetch(url, {
        method: editingId ? "PUT" : "POST",
        body: JSON.stringify(body),
      })
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`)
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tag-categories", orgId] })
      // Pulldown caches need to refresh too — they fetch from the same catalog.
      queryClient.invalidateQueries({
        queryKey: ["distinct-tag-categories"],
      })
      showToast(
        editingId
          ? t("tagCategories.toast.updated", "Category updated")
          : t("tagCategories.toast.created", "Category created"),
        t("tagCategories.toast.saved", "Saved."),
        "success",
      )
      setIsOpen(false)
      resetDraft()
    },
    onError: (err: unknown) => {
      showToast(
        t("tagCategories.toast.errorTitle", "Could not save category"),
        err instanceof Error ? err.message : String(err),
        "error",
      )
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!orgId) throw new Error("No organisation selected")
      const res = await authedFetch(
        `/api/v1/organizations/${orgId}/tag-categories/${id}`,
        { method: "DELETE" },
      )
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tag-categories", orgId] })
      queryClient.invalidateQueries({
        queryKey: ["distinct-tag-categories"],
      })
      showToast(
        t("tagCategories.toast.deleted", "Category deleted"),
        t(
          "tagCategories.toast.deletedBody",
          "Tags that referenced it have been moved to the default category.",
        ),
        "success",
      )
      setIsOpen(false)
      resetDraft()
    },
    onError: (err: unknown) => {
      showToast(
        t("tagCategories.toast.deleteErrorTitle", "Could not delete"),
        err instanceof Error ? err.message : String(err),
        "error",
      )
    },
  })

  return (
    <Box>
      <Flex mb={4} align="center" gap={3}>
        <VisionButton
          variant="ghost"
          size="sm"
          onClick={() => navigate({ to: "/admin-tags" })}
        >
          <FiArrowLeft />
          <Text ml={2}>{t("tagCategories.back", "Back to Tags")}</Text>
        </VisionButton>
      </Flex>

      <Flex align="center" gap={3} mb={6} flexWrap="wrap">
        <IconBox variant="glass" size="lg">
          <FiFolder fontSize="24px" color={sc.iconColor} />
        </IconBox>
        <Box flex="1">
          <Heading size="lg" color={sc.heading}>
            {t("tagCategories.title", "Tag Categories")}
          </Heading>
          <Text color={sc.textMuted}>
            {t(
              "tagCategories.subtitle",
              "Org-scoped catalog of tag categories. The default row can't be deleted — tags whose category is removed are reassigned to the default automatically.",
            )}
          </Text>
        </Box>
      </Flex>

      <VisionCard>
        <Flex p={4} align="center" justify="space-between" flexWrap="wrap">
          <Heading size="md" color={sc.heading}>
            {t("tagCategories.listTitle", "{{count}} total", {
              count: listQuery.data?.count ?? 0,
            })}
          </Heading>
          <VisionButton
            variant="brand"
            size="sm"
            onClick={() => {
              resetDraft()
              setIsOpen(true)
            }}
          >
            <FiPlus />
            <Text ml={1}>{t("tagCategories.add", "Add category")}</Text>
          </VisionButton>
        </Flex>

        {listQuery.isLoading ? (
          <Flex justify="center" py={6}>
            <Spinner size="md" color={sc.spinnerColor} />
          </Flex>
        ) : rows.length === 0 ? (
          <Text color={sc.textMuted} fontSize="sm" px={4} pb={4}>
            {t("tagCategories.empty", "No categories yet.")}
          </Text>
        ) : (
          <Box overflowX="auto">
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("tagCategories.col.category", "Category")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("tagCategories.col.default", "Default")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader w="40px" />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {rows.map((row) => (
                  <Table.Row key={row.id}>
                    <Table.Cell color={sc.heading} fontSize="sm">
                      {row.name}
                    </Table.Cell>
                    <Table.Cell>
                      {row.is_default && (
                        <Badge colorPalette="blue" size="sm">
                          {t("tagCategories.defaultBadge", "default")}
                        </Badge>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <VisionButton
                        variant="ghost"
                        size="xs"
                        onClick={() => seedFromRow(row)}
                      >
                        <FiEdit2 />
                      </VisionButton>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        )}
      </VisionCard>

      <DialogRoot
        open={isOpen}
        onOpenChange={(d) => {
          if (!d.open) resetDraft()
          setIsOpen(d.open)
        }}
        size="md"
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingId
                ? t("tagCategories.dialog.editTitle", "Edit category")
                : t("tagCategories.dialog.createTitle", "Add category")}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Stack gap={3}>
              <Box>
                <Text fontSize="sm" color={sc.textMuted} mb={1}>
                  {t("tagCategories.field.name", "Name")}
                </Text>
                <Input
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  placeholder="e.g. Apparel"
                  maxLength={100}
                  autoFocus
                />
              </Box>
              {editingIsDefault && (
                <Text fontSize="xs" color={sc.textMuted}>
                  {t(
                    "tagCategories.field.defaultNote",
                    "This is the default category. It can be renamed but not deleted — tags whose category is removed are reassigned to it.",
                  )}
                </Text>
              )}
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Flex gap={2} w="100%" justify="space-between">
              <Box>
                {editingId && !editingIsDefault && (
                  <VisionButton
                    variant="ghost"
                    size="sm"
                    colorPalette="red"
                    disabled={deleteMutation.isPending}
                    onClick={() => {
                      if (
                        confirm(
                          t(
                            "tagCategories.confirmDelete",
                            "Delete this category? Tags using it will be moved to the default.",
                          ) as string,
                        )
                      ) {
                        deleteMutation.mutate(editingId)
                      }
                    }}
                  >
                    <FiTrash2 />
                    <Text ml={1}>
                      {t("tagCategories.dialog.delete", "Delete")}
                    </Text>
                  </VisionButton>
                )}
              </Box>
              <Flex gap={2}>
                <VisionButton
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setIsOpen(false)
                    resetDraft()
                  }}
                >
                  {t("tagCategories.dialog.cancel", "Cancel")}
                </VisionButton>
                <VisionButton
                  variant="brand"
                  size="sm"
                  disabled={
                    draftName.trim().length === 0 || saveMutation.isPending
                  }
                  onClick={() => saveMutation.mutate()}
                >
                  {t("tagCategories.dialog.save", "Save")}
                </VisionButton>
              </Flex>
            </Flex>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </Box>
  )
}
